import React, { useEffect, useState } from 'react';
import { Search, X, ChevronDown, ChevronLeft, ChevronRight, ExternalLink, Building2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface AdvancedSearchProps {
  onResultClick: () => void;
}
export function AdvancedSearch({
  onResultClick
}: AdvancedSearchProps) {
  const [companyName, setCompanyName] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalStage, setModalStage] = useState<'connecting' | 'matched' | 'layering' | 'ready'>('connecting');
  const [foundItems, setFoundItems] = useState<number>(0);
  const handleViewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowModal(true);
    setModalStage('connecting');
    setFoundItems(0);
    // After 1.5 seconds, show entity matched
    setTimeout(() => {
      setModalStage('matched');
      // After 1.5 seconds, show layering additional sources
      setTimeout(() => {
        setModalStage('layering');
        // Cascade through finding each item
        const dataItems = 4;
        for (let i = 1; i <= dataItems; i++) {
          setTimeout(() => {
            setFoundItems(i);
          }, i * 400); // 400ms between each checkmark
        }
        // After all items are found (1.6s) + brief pause (800ms), show ready
        setTimeout(() => {
          setModalStage('ready');
          // After 1.5 seconds, close modal and navigate
          setTimeout(() => {
            setShowModal(false);
            onResultClick();
          }, 1500);
        }, dataItems * 400 + 800);
      }, 1500);
    }, 1500);
  };
  useEffect(() => {
    const targetText = 'Dunder Mifflin';
    let currentIndex = 0;
    // Start typing after a short delay
    const startDelay = setTimeout(() => {
      const typingInterval = setInterval(() => {
        if (currentIndex <= targetText.length) {
          setCompanyName(targetText.slice(0, currentIndex));
          currentIndex++;
        } else {
          clearInterval(typingInterval);
          // Trigger search after typing finishes
          setTimeout(() => {
            setIsSearching(true);
            setTimeout(() => {
              setIsSearching(false);
              setShowResults(true);
            }, 800);
          }, 400);
        }
      }, 80); // Typing speed
      return () => clearInterval(typingInterval);
    }, 500);
    return () => clearTimeout(startDelay);
  }, []);
  return <div className="px-8 py-6 max-w-6xl mx-auto w-full" data-id="element-249" data-mp="21p5egblbh6mg">
      <h1 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-250" data-mp="21qzxfkd12onw">
        Advanced Company Search
      </h1>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6" data-id="element-251" data-mp="1wp0x6jcl47v6">
        <div className="p-6 space-y-4" data-id="element-252" data-mp="3d39adnbgt4u5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-253" data-mp="385hginr9f8nb">
            {/* Company Name */}
            <div className="relative" data-id="element-254" data-mp="3fp0477bg7q28">
              <label className="absolute -top-2 left-3 bg-white px-1 text-xs font-medium text-indigo-600 z-10" data-id="element-255" data-mp="3fnysr1qit1jz">
                Company Name *
              </label>
              <div className="relative" data-id="element-256" data-mp="36obtoml9k7u3">
                <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)} className="w-full px-4 py-3 border-2 border-indigo-500 rounded-lg focus:outline-none text-gray-900" data-id="element-257" data-mp="36pn598kyajvr" />
                {companyName && <button onClick={() => setCompanyName('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-258" data-mp="7bb0y10wopy4">
                    <X className="w-5 h-5" data-id="element-259" data-mp="3tmf08uw7iw36" />
                  </button>}
              </div>
            </div>

            {/* Company Website */}
            <div data-id="element-260" data-mp="q0hny08qg3pq">
              <input type="text" placeholder="Company Website" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 placeholder-gray-500" data-id="element-261" data-mp="q0zi8rbu47v0" />
            </div>

            {/* Country */}
            <div className="relative" data-id="element-262" data-mp="2khhm6lrgegab">
              <label className="absolute -top-2 left-3 bg-white px-1 text-xs font-medium text-gray-500 z-10" data-id="element-263" data-mp="2kikgz4rwd2vi">
                Country
              </label>
              <div className="relative" data-id="element-264" data-mp="3c7nkgibym973">
                <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 appearance-none bg-white" data-id="element-265" data-mp="3c8p3pa34q1zw">
                  <option data-id="element-266" data-mp="33fln4sx6t911">United States</option>
                  <option data-id="element-267" data-mp="3svr36bx5ao4">Canada</option>
                  <option data-id="element-268" data-mp="3tx0l6dadp3q">United Kingdom</option>
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-gray-400 pointer-events-none" data-id="element-269" data-mp="3v5yrt4jeg3nh">
                  <X className="w-4 h-4" data-id="element-270" data-mp="1hq8415kxrrue" />
                  <ChevronDown className="w-4 h-4" data-id="element-271" data-mp="1mmyjprstwlfa" />
                </div>
              </div>
            </div>

            {/* State */}
            <div className="relative" data-id="element-272" data-mp="1qoa9pyfm78o0">
              <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 appearance-none bg-white" data-id="element-273" data-mp="1vo544bylmgdt">
                <option value="" disabled selected hidden data-id="element-274" data-mp="25mchylxlcibl">
                  State
                </option>
                <option data-id="element-275" data-mp="2adb25wlpa0k4">Pennsylvania</option>
                <option data-id="element-276" data-mp="2adksxmekkbqv">Connecticut</option>
                <option data-id="element-277" data-mp="2eemrs24kqlsb">New York</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-278" data-mp="2eduxcumpqu3m" />
            </div>
          </div>

          {/* Street Address */}
          <div className="relative" data-id="element-279" data-mp="2odc0p5ehsvwf">
            <input type="text" placeholder="Street Address" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 placeholder-gray-500" data-id="element-280" data-mp="oakl3uj092sw" />
            <Building2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300 pointer-events-none" data-id="element-281" data-mp="y2778lwvsmaf" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-282" data-mp="170asdh3yfh1p">
            {/* City */}
            <div className="relative" data-id="element-283" data-mp="1300ap76sv270">
              <input type="text" placeholder="City" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 placeholder-gray-500" data-id="element-284" data-mp="12yxi8uttwroz" />
              <Building2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300 pointer-events-none" data-id="element-285" data-mp="3ir4yaaa8bene" />
            </div>

            {/* Postal Code */}
            <div className="relative" data-id="element-286" data-mp="1t97rrff6yrz6">
              <input type="text" placeholder="Postal Code" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 placeholder-gray-500" data-id="element-287" data-mp="1ta9ckbm0esu3" />
              <Building2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300 pointer-events-none" data-id="element-288" data-mp="1ogy7og0rcm4t" />
            </div>
          </div>

          {/* Search Button */}
          <button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 rounded-lg flex items-center justify-center gap-2 transition-colors mt-2" disabled={isSearching} data-id="element-289" data-mp="1fjnswkoqeev5">
            {isSearching ? <motion.div animate={{
            rotate: 360
          }} transition={{
            duration: 1,
            repeat: Infinity,
            ease: 'linear'
          }} className="w-5 h-5 border-2 border-white border-t-transparent rounded-full" data-id="element-290" data-mp="2uyla0i0xtds1" /> : <>
                <Search className="w-5 h-5" data-id="element-291" data-mp="1dy8640w0nb9m" />
                SEARCH
              </>}
          </button>
        </div>

        {/* Help Section */}
        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between text-sm" data-id="element-292" data-mp="18xkbl58kwcan">
          <div className="text-gray-600" data-id="element-293" data-mp="13y8vlyg1dau4">
            Need help finding a company?{' '}
            <a href="#" className="text-indigo-600 hover:underline" data-id="element-294" data-mp="10132s8nosqi2">
              Contact search support
            </a>
          </div>
          <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-295" data-mp="q1c4rwzx3td8" />
        </div>
      </div>

      {/* Results Section */}
      <AnimatePresence data-id="element-296" data-mp="2pe6jggy88jgn">
        {showResults && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-4" data-id="element-297" data-mp="2pbkbwhbc63yf">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm px-6 py-3 flex items-center justify-between" data-id="element-298" data-mp="2wwnvattksacs">
              <span className="text-gray-700" data-id="element-299" data-mp="31ph2rpgfhwsk">Showing results 1 to 2</span>
              <div className="flex items-center gap-2" data-id="element-300" data-mp="36nika5y7ho1x">
                <button className="px-3 py-1.5 text-sm text-gray-400 flex items-center gap-1 cursor-not-allowed" data-id="element-301" data-mp="36p3efuglz4z9">
                  <ChevronLeft className="w-4 h-4" data-id="element-302" data-mp="2dyl90msewrio" />
                  PREVIOUS PAGE
                </button>
                <button className="px-3 py-1.5 text-sm text-indigo-600 border border-indigo-200 rounded flex items-center gap-1 hover:bg-indigo-50" data-id="element-303" data-mp="2ixy0xly54os4">
                  NEXT PAGE
                  <ChevronRight className="w-4 h-4" data-id="element-304" data-mp="2nueoof1fh14o" />
                </button>
              </div>
            </div>

            {/* Result 1 - HQ (Clickable) */}
            <motion.div whileHover={{
          scale: 1.005
        }} onClick={handleViewClick} className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 cursor-pointer hover:border-indigo-300 hover:bg-indigo-100 hover:shadow-md transition-all relative overflow-hidden group" data-id="element-305" data-mp="34yzfohuxkh5">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500" data-id="element-306" data-mp="2v39m5bumotmn" />
              <div className="pl-4" data-id="element-307" data-mp="1yda5eu4bqosz">
                <div className="flex items-center gap-2 mb-1" data-id="element-308" data-mp="1yebtbz6fv5s4">
                  <h3 className="text-lg font-medium text-gray-900 group-hover:text-indigo-600 transition-colors" data-id="element-309" data-mp="22evqv3uxcbes">
                    DUNDER MIFFLIN PAPER COMPANY
                  </h3>
                  <ExternalLink className="w-4 h-4 text-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-310" data-mp="27gbbwvx3qhx6" />
                </div>
                <p className="text-indigo-600 text-sm mb-3" data-id="element-311" data-mp="3itx1ujascnjr">
                  1725 SLOUGH AVENUE, SCRANTON, PA, 18505
                </p>
                <div className="flex items-center justify-between" data-id="element-312" data-mp="3nqdp7ba5uxvs">
                  <div className="flex items-center gap-2" data-id="element-313" data-mp="1c3tpnjj34xjl">
                    <span className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-full" data-id="element-314" data-mp="1761vsjyvr1cr">
                      Active
                    </span>
                    <span className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-full" data-id="element-315" data-mp="xfyt1s1yi918">
                      HQ
                    </span>
                  </div>
                  <motion.button whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} onClick={handleViewClick} className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg transition-colors" data-id="element-316" data-mp="ofahr53y6ms1">
                    VIEW
                  </motion.button>
                </div>
              </div>
            </motion.div>

            {/* Result 2 - Branch */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 relative overflow-hidden hover:bg-indigo-50/30 transition-all" data-id="element-317" data-mp="7455zombobsy">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500" data-id="element-318" data-mp="3te7oryhu938w" />
              <div className="pl-4" data-id="element-319" data-mp="26c1r14kxrvb">
                <div className="flex items-center gap-2 mb-1" data-id="element-320" data-mp="27eoqxys7a5y">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-321" data-mp="pw6cnd5c96cx">
                    DUNDER MIFFLIN STAMFORD
                  </h3>
                </div>
                <p className="text-indigo-600 text-sm mb-3" data-id="element-322" data-mp="utg7j8bhya85">
                  850 CANAL ST, STAMFORD, CT, 06902
                </p>
                <div className="flex items-center justify-between" data-id="element-323" data-mp="kwtq6tidq1av">
                  <div className="flex items-center gap-2" data-id="element-324" data-mp="kz7jawti13fs">
                    <span className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-full" data-id="element-325" data-mp="31l7d2ucqkps7">
                      Active
                    </span>
                    <span className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-full" data-id="element-326" data-mp="2y31irlegej3b">
                      Branch
                    </span>
                  </div>
                  <motion.button whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} onClick={onResultClick} className="px-6 py-2.5 bg-white border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50 text-sm font-semibold rounded-lg transition-colors" data-id="element-327" data-mp="380xt4azkha0d">
                    ADD
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Data Discovery Modal */}
      <AnimatePresence data-id="element-328" data-mp="1ro0m1dhltwvo">
        {showModal && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/50 z-50" data-id="element-329" data-mp="uwlza38856dw" />

            {/* Modal */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-330" data-mp="18oa73ubfx8p2">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8" data-id="element-331" data-mp="1rltl7ccr9hic">
                {modalStage === 'connecting' && <div className="text-center" data-id="element-332" data-mp="3oh98lrrq4d01">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 1,
                repeat: Infinity,
                ease: 'linear'
              }} className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full mx-auto mb-6" data-id="element-333" data-mp="3tdg4erqeo849" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-334" data-mp="373r73by3mndc">
                      Connecting to your preferred bureau
                    </h3>
                    <p className="text-gray-600" data-id="element-335" data-mp="3q58027d9k60">Searching for company...</p>
                  </div>}

                {modalStage === 'matched' && <div className="text-center" data-id="element-336" data-mp="3qu3apuzylpal">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-337" data-mp="3qta02ixvqfn6">
                      <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-338" data-mp="2je58apln2a5v">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-339" data-mp="2lvg5mejf886f" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-340" data-mp="1y5luc1er2roa">
                      Entity Matched
                    </h3>
                    <p className="text-gray-600" data-id="element-341" data-mp="22wu26t0fm4zc">
                      Dunder Mifflin Paper Company verified
                    </p>
                  </div>}

                {modalStage === 'layering' && <div className="text-center" data-id="element-342" data-mp="1t44u8843b98r">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 1,
                repeat: Infinity,
                ease: 'linear'
              }} className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full mx-auto mb-6" data-id="element-343" data-mp="1f67vb525yqqx" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-344" data-mp="19lqus1twpd5j">
                      Layering additional data sources
                    </h3>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-345" data-mp="1jhttexpjutnq">
                      Enriching bureau data with:
                    </p>
                    <div className="space-y-3 text-left" data-id="element-346" data-mp="1eja88zis5lwf">
                      {['People', 'Private Financials', 'Recent News', 'Social & Web Presence'].map((item, index) => <motion.div key={item} initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.2
                }} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-id="element-347" data-mp="229e94z4l37jq" data-mpi={index}>
                          {foundItems > index ? <motion.div initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} transition={{
                    type: 'spring',
                    stiffness: 500,
                    damping: 30
                  }} className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-348" data-mp="2pxbcp7kqyvyg">
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-349" data-mp="1up0s5jc0x80f">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-350" data-mp="1gqj8pxt5ihob" />
                              </svg>
                            </motion.div> : <div className="w-2 h-2 bg-indigo-600 rounded-full" data-id="element-351" data-mp="x0z2mv4g7njz" />}
                          <span className={`font-medium transition-colors ${foundItems > index ? 'text-gray-900' : 'text-gray-600'}`} data-id="element-352" data-mp="t06slznr5emn">
                            {item}
                          </span>
                        </motion.div>)}
                    </div>
                  </div>}

                {modalStage === 'ready' && <div className="text-center" data-id="element-353" data-mp="3010qg45zvocg">
                    <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-354" data-mp="2v2quhrcg4jps">
                      <svg className="w-8 h-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-355" data-mp="2qapebuc5q8uh">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-356" data-mp="2h8zmined57w6" />
                      </svg>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-357" data-mp="rtf8gzmpmdz9">
                      Report Ready
                    </h3>
                    <p className="text-gray-600" data-id="element-358" data-mp="wu37o8l3e14k">Opening account view...</p>
                  </div>}
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}